import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Button } from "./Button";
import { Eyebrow } from "./Eyebrow";

/**
 * Alternating full-width feature row used on the Divisions page.
 * Visual panel = abstract gradient + division icon (no stock photos).
 */
export const FeatureRow = ({
    eyebrow,
    title,
    paragraphs = [],
    capabilities = [],
    cta,
    icon: Icon,
    logoSrc,
    logoAlt,
    logoMaxWidth = 320,
    reverse = false,
    "data-testid": testId,
}) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16"
            data-testid={testId}
        >
            {/* Visual panel */}
            <div
                className={`relative aspect-[4/3] overflow-hidden rounded-2xl border border-border-subtle bg-elevated lg:col-span-6 ${
                    reverse ? "lg:order-2" : "lg:order-1"
                }`}
            >
                <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse at 30% 30%, rgba(124,255,79,0.10) 0%, rgba(124,255,79,0.02) 30%, transparent 60%)",
                        filter: "blur(20px)",
                    }}
                />
                <div className="zen-grid opacity-70" />
                <div className="absolute inset-0 flex items-center justify-center p-6">
                    {logoSrc ? (
                        <img
                            src={logoSrc}
                            alt={logoAlt || ""}
                            loading="lazy"
                            className="object-contain"
                            style={{
                                maxWidth: `${logoMaxWidth}px`,
                                width: "100%",
                                height: "auto",
                                filter: "drop-shadow(0 0 40px rgba(124,255,79,0.08))",
                            }}
                        />
                    ) : (
                        Icon && (
                            <div
                                className="flex h-28 w-28 items-center justify-center rounded-2xl border border-border-default bg-[rgba(255,255,255,0.02)] backdrop-blur-sm"
                                style={{
                                    boxShadow:
                                        "0 0 60px rgba(124,255,79,0.08), inset 0 0 30px rgba(124,255,79,0.03)",
                                }}
                            >
                                <Icon
                                    className="h-12 w-12 text-[var(--accent)]"
                                    strokeWidth={1.25}
                                />
                            </div>
                        )
                    )}
                </div>
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background:
                            "linear-gradient(to bottom right, transparent 60%, rgba(0,0,0,0.4))",
                    }}
                />
            </div>

            {/* Copy */}
            <div
                className={`lg:col-span-6 ${
                    reverse ? "lg:order-1" : "lg:order-2"
                }`}
            >
                <Eyebrow>{eyebrow}</Eyebrow>
                <h2 className="mt-4 font-display text-[clamp(28px,3.4vw,42px)] font-semibold tracking-tight-2 leading-[1.1] text-text-primary">
                    {title}
                </h2>
                <div className="mt-6 space-y-4">
                    {paragraphs.map((p, i) => (
                        <p
                            key={i}
                            className="text-[16px] leading-[1.7] text-text-secondary"
                        >
                            {p}
                        </p>
                    ))}
                </div>
                {capabilities.length > 0 && (
                    <ul className="mt-8 space-y-3">
                        {capabilities.map((c) => (
                            <li
                                key={c}
                                className="flex items-start gap-3 text-[15px] text-text-primary"
                            >
                                <span
                                    className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-[rgba(124,255,79,0.25)] bg-[rgba(124,255,79,0.06)]"
                                    aria-hidden
                                >
                                    <Check
                                        className="h-3 w-3 text-[var(--accent)]"
                                        strokeWidth={2}
                                    />
                                </span>
                                <span>{c}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {cta && (
                    <div className="mt-10">
                        <Button
                            to={cta.external ? undefined : cta.href}
                            href={cta.external ? cta.href : undefined}
                            external={cta.external}
                            variant="subtle"
                            size="md"
                            data-testid={`${testId}-cta`}
                        >
                            {cta.label}
                        </Button>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default FeatureRow;
